import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getImageUrl } from '../../utils/api';

interface UserHeaderProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    mobileShow: boolean;
    setMobileShow: (show: boolean) => void;
}

const UserHeader: React.FC<UserHeaderProps> = ({ collapsed, setCollapsed, mobileShow, setMobileShow }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [dropdown, setDropdown] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getPageTitle = () => {
        const path = window.location.pathname;
        const items: {[key: string]: string} = {
            '/broker-dashboard': 'Dashboard',
            '/broker-classified-ads': 'Classified Leads',
            '/saved-classified-leads': 'Saved Leads',
            '/broker-profile': 'My Profile',
            '/broker-add-project': 'Add Project',
            '/broker-my-projects': 'My Projects',
            '/broker-wishlist': 'Wishlist',
            '/broker-leads': 'Leads',
            '/broker-inquiries': 'Inquiries',
            '/broker-buy-lead-plan': 'Lead Plan',
            '/broker-purchased-plans': 'Purchased Plans',
            '/broker-change-password': 'Security',
            '/developer-dashboard': 'Dashboard',
            '/my-profile': 'My Profile',
            '/bookmark-list': 'Wishlist',
            '/submit-property-dashboard': 'Add Property',
            '/my-property': 'My Properties',
            '/owner/dashboard': 'Dashboard',
            '/owner/profile': 'My Profile',
            '/owner-property': 'My Properties',
            '/owner/my-property': 'My Properties',
            '/owner/your-property': 'Your Property',
            '/owner/edit-property': 'Edit Property',
            '/owner/add-property': 'Add Property',
            '/owner-add-property': 'Add Property',
            '/owner/change-password': 'Security',
        };
        const title = items[path] || (path.startsWith('/owner/edit-property') ? 'Edit Property' : 'System Console');
        return title;
    };

    return (
        <header className="sticky top-0 z-40 h-20 flex items-center justify-between px-6 md:px-10 border-b border-white/[0.05] shrink-0 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>
            <div className="flex items-center gap-6">
                <button
                    className="hidden lg:flex items-center justify-center w-12 h-12 rounded-2xl bg-white/5 text-white hover:bg-[#ffbb00] hover:text-[#05144b] transition-all transform active:scale-90 shadow-sm border border-white/5"
                    onClick={() => setCollapsed(!collapsed)}
                    title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    <i className={`fa-solid ${collapsed ? 'fa-indent' : 'fa-outdent'} text-lg`}></i>
                </button>
                <button
                    className="lg:hidden flex items-center justify-center w-12 h-12 rounded-2xl bg-indigo-600 text-white transition-all shadow-lg shadow-indigo-600/20 active:scale-90"
                    onClick={() => setMobileShow(!mobileShow)}
                >
                    <i className="fa-solid fa-bars-staggered text-lg"></i>
                </button>
                <div className="hidden md:block">
                    <div className="flex flex-col">
                         <h1 className="text-[20px] font-black text-white flex items-center gap-3 uppercase tracking-tight">
                            <span className="w-1.5 h-6 bg-[#ffbb00] rounded-full shadow-[0_0_15px_rgba(255,187,0,0.4)]"></span>
                            {getPageTitle()}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4 md:gap-8">
                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className="w-12 h-12 rounded-2xl bg-white/5 text-white hover:bg-[#ffbb00] hover:text-[#05144b] transition-all flex items-center justify-center shadow-sm border border-white/5 group overflow-hidden relative"
                    title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                >
                    <div className="relative z-10">
                        <i className={`fa-solid ${theme === 'light' ? 'fa-moon' : 'fa-sun'} text-lg transition-transform duration-500 group-hover:rotate-[360deg]`}></i>
                    </div>
                    <div className="absolute inset-0 bg-[#ffbb00] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>

                {/* Notifications */}
                <div className="hidden md:flex items-center gap-4">
                    <button className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 text-white hover:bg-[#ffbb00] hover:text-[#05144b] transition-all border border-white/5 relative shadow-sm group">
                        <i className="fa-solid fa-bell text-lg group-hover:shake"></i>
                        <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-[#05144b]"></span>
                    </button>
                </div>

                {/* Profile Trigger */}
                <div className="relative">
                    <div
                        onClick={() => setDropdown(!dropdown)}
                        className="flex items-center gap-4 p-1.5 pr-4 rounded-[1.8rem] bg-white/5 hover:bg-white/10 transition-all cursor-pointer border border-white/5 shadow-sm group active:scale-[0.98]"
                    >
                        <div className="relative">
                            {user?.avatar ? (
                                <img src={getImageUrl(user.avatar)} className="w-10 h-10 md:w-11 md:h-11 rounded-[1.2rem] object-cover ring-4 ring-[#05144b] shadow-xl group-hover:scale-105 transition-all" alt="Profile" />
                            ) : (
                                <div className="w-10 h-10 md:w-11 md:h-11 rounded-[1.2rem] bg-gradient-to-br from-[#05144b] to-[#4c1d95] flex items-center justify-center text-white font-black text-sm uppercase ring-4 ring-[#05144b] shadow-xl group-hover:scale-105 transition-all">
                                    {user?.name?.charAt(0) || 'U'}
                                </div>
                            )}
                            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 border-2 border-[#05144b] rounded-full shadow-sm"></div>
                        </div>
                        <div className="hidden sm:block text-left">
                            <h6 className="text-sm font-bold text-white leading-tight tracking-tight">{user?.name?.split(' ')[0] || 'User'}</h6>
                            <div className="flex items-center gap-1.5 mt-1">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                <p className="text-[11px] font-semibold text-blue-100/70 leading-none capitalize">{user?.role || 'User'}</p>
                            </div>
                        </div>
                        <i className={`fa-solid fa-chevron-down text-[10px] text-white transition-transform duration-500 ${dropdown ? 'rotate-180' : ''}`}></i>
                    </div>

                    {dropdown && (
                        <>
                            <div className="fixed inset-0 z-[100]" onClick={() => setDropdown(false)}></div>
                            <div className="absolute right-0 mt-3 w-64 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-[101]" style={{ background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)' }}>
                                <div className="px-5 py-3.5 border-b border-white/5 bg-white/5">
                                     <div className="font-bold text-white text-base mb-0.5">{user?.name || 'User'}</div>
                                     <div className="text-blue-100/60 text-xs font-medium truncate">{user?.email}</div>
                                </div>
                                
                                <div className="p-2 space-y-0.5">
                                    <Link 
                                        to="/my-profile" 
                                        className="flex items-center gap-3 px-4 py-2 text-blue-100/80 hover:bg-white/10 hover:text-white rounded-xl transition-all font-semibold text-base group"
                                        onClick={() => setDropdown(false)}
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-all">
                                            <i className="fa-solid fa-user-gear text-base transition-transform group-hover:scale-110"></i>
                                        </div>
                                         Edit Profile
                                    </Link>
                                    <Link 
                                        to="/change-password" 
                                        className="flex items-center gap-3 px-4 py-2 text-blue-100/80 hover:bg-white/10 hover:text-white rounded-xl transition-all font-semibold text-base group"
                                        onClick={() => setDropdown(false)}
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-all">
                                            <i className="fa-solid fa-shield-halved text-base transition-transform group-hover:scale-110"></i>
                                        </div>
                                        Security
                                    </Link>
                                </div>

                                <div className="p-2 border-t border-white/5 bg-white/[0.02]">
                                    <button 
                                        className="flex items-center gap-3 w-full px-4 py-2 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 rounded-xl transition-all font-semibold text-base group"
                                        onClick={() => { handleLogout(); setDropdown(false); }}
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-all">
                                            <i className="fa-solid fa-power-off text-base transition-transform group-hover:rotate-12"></i>
                                        </div>
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};

export default UserHeader;
